// Dashboard card: yearly reservation counts grouped by event type, rendered as
// horizontal ranked bars with the cumulated amount as a secondary hint.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Divider, Flex, Spinner, Text, useColorModeValue, useToken } from "@chakra-ui/react";
import { MdCelebration } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { eventTypeLabel } from "constants/eventTypeLabels";
import CardHeader from "../shared/CardHeader";
import RankBars from "../shared/RankBars";
import YearNavFooter from "../shared/YearNavFooter";

/** Chakra color tokens, one per rendered row: 8 event types + the "Autres" tail. */
const PALETTE = [
  "brand.400", "blue.500", "green.500", "orange.500", "red.500",
  "teal.400", "pink.400", "navy.300", "secondaryGray.500"
];

/** Number of event types shown individually before the tail is aggregated. */
const TOP_COUNT = 8;

/** Thousand-separated formatter for the cumulated amounts (API sends no currency). */
const AMOUNT_FORMAT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

/**
 * Sums the counts and amounts of the stats rows left out of the top slice.
 * @param {Array} rest - stats rows ([{event, count, total_amount}])
 * @returns {{count: number, total: number}} aggregated tail
 */
function sumRest(rest) {
  return rest.reduce(
    (acc, row) => ({
      count: acc.count + (row.count || 0),
      total: acc.total + (row.total_amount || 0)
    }),
    { count: 0, total: 0 }
  );
}

/**
 * Builds the ranked bar items: the top event types plus an aggregated "Autres"
 * row when the API returned more types than the top slice holds.
 * @param {Array} stats - API rows ([{event, count, total_amount}]), count desc
 * @param {Array} colors - resolved colors, one per rendered row
 * @returns {Array} [{label, value, hint, color}] ready for RankBars
 */
function buildEventItems(stats, colors) {
  const top = stats.slice(0, TOP_COUNT).map((row, index) => ({
    label: eventTypeLabel(row.event) || "—",
    value: row.count || 0,
    hint: AMOUNT_FORMAT.format(row.total_amount || 0),
    color: colors[index]
  }));
  if (stats.length <= TOP_COUNT) return top;
  const rest = sumRest(stats.slice(TOP_COUNT));
  return top.concat({
    label: `Autres (${stats.length - TOP_COUNT})`,
    value: rest.count,
    hint: AMOUNT_FORMAT.format(rest.total),
    color: colors[TOP_COUNT]
  });
}

/**
 * Renders the card body: a spinner while loading, a discreet message on error
 * or on a year without reservations, the ranked bars otherwise.
 * @param {Object} props - loading (bool), error (string|null), items (array)
 * @returns {React.ReactElement}
 */
function EventTypesBody({ loading, error, items }) {
  const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  if (loading) return <Flex justify="center" py="6"><Spinner color="brand.400" /></Flex>;
  if (error || !items.length) {
    return (
      <Text textAlign="center" py="6" fontSize="sm" color={mutedColor}>
        {error || "Aucune réservation à afficher"}
      </Text>
    );
  }
  return <RankBars items={items} />;
}

/**
 * Displays the reservation breakdown by event type for a given year, allowing
 * year navigation. Backed by GET /admin/reservation/stats_by_event?year=.
 * @returns {React.ReactElement}
 */
export default function EventTypesCard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState([]);
  const colors = useToken("colors", PALETTE);
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  const load = useCallback(async (targetYear) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get("/admin/reservation/stats_by_event", { params: { year: targetYear } });
      setStats(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error("Error fetching event type stats:", requestError);
      setError("Stats par type d'événement indisponibles");
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeYear = useCallback((delta) => {
    const next = year + delta;
    setYear(next);
    load(next);
  }, [year, load]);

  useEffect(() => {
    load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card h="100%">
      <CardHeader icon={MdCelebration} title="Réservations par type d'événement" accent="brand.400"
        right={<Text fontSize="xs" color={subColor}>{year}</Text>} />
      <Divider mb="8px" />
      <EventTypesBody loading={loading} error={error} items={buildEventItems(stats, colors)} />
      <YearNavFooter loading={loading} onPrev={() => changeYear(-1)} onNext={() => changeYear(1)}
        onRefresh={() => load(year)} />
    </Card>
  );
}
